import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, X, ClipboardPaste } from "lucide-react";
import { toast } from "sonner";

interface QuickTag {
  label: string;
  insertText: string;
}

interface TextInputProps {
  onSubmit: (text: string) => void;
  onCancel: () => void;
  disabled?: boolean;
  quickTags?: QuickTag[];
}

export function TextInput({ onSubmit, onCancel, disabled, quickTags }: TextInputProps) {
  const [text, setText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleQuickTag = (insertText: string) => {
    const textarea = textareaRef.current;
    const prefix = text && !text.endsWith("\n") ? "\n" : "";
    const newText = text + prefix + insertText;
    setText(newText);
    // Posicionar cursor no final após render
    requestAnimationFrame(() => {
      if (textarea) {
        textarea.focus();
        const pos = newText.length;
        textarea.setSelectionRange(pos, pos);
        textarea.scrollTop = textarea.scrollHeight;
      }
    });
  };

  useEffect(() => {
    // Auto-focus no textarea quando componente monta
    textareaRef.current?.focus();

    // Atalho de teclado: Escape para cancelar
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onCancel]);

  const handlePaste = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      setText(clipboardText);
      toast.success("Texto colado com sucesso!");
    } catch (error) {
      toast.error("Não foi possível acessar a área de transferência");
    }
  };

  const handleSubmit = () => {
    const trimmedText = text.trim();
    
    if (trimmedText.length < 10) {
      toast.error("Digite pelo menos 10 caracteres");
      return;
    }
    
    if (trimmedText.length > 10000) {
      toast.error("Texto muito longo. Máximo 10.000 caracteres");
      return;
    }

    onSubmit(trimmedText);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Ctrl+Enter para enviar
    if (e.ctrlKey && e.key === "Enter") {
      handleSubmit();
    }
  };

  const characterCount = text.length;
  const isValid = characterCount >= 10 && characterCount <= 10000;
  const isWarning = characterCount > 0 && characterCount < 50;
  const isExcess = characterCount > 10000;

  const getCounterColor = () => {
    if (isExcess) return "text-destructive";
    if (isWarning) return "text-yellow-600";
    if (isValid) return "text-green-600";
    return "text-muted-foreground";
  };

  return (
    <div className="animate-fade-in space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Entrada de Texto Livre</h3>
          <p className="text-sm text-muted-foreground">
            Digite ou cole suas observações clínicas
          </p>
        </div>
        <span className={`text-sm font-medium ${getCounterColor()}`}>
          {characterCount}/10000
        </span>
      </div>

      {quickTags && quickTags.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            Resultados recentes do paciente:
          </p>
          <div className="flex flex-wrap gap-2">
            {quickTags.map((tag) => (
              <Badge
                key={tag.label}
                variant="secondary"
                onClick={() => !disabled && handleQuickTag(tag.insertText)}
                className="cursor-pointer hover:bg-primary/10 hover:text-primary transition-colors"
              >
                {tag.label}
              </Badge>
            ))}
          </div>
        </div>
      )}



      <Textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={`Digite aqui suas observações clínicas...

Exemplos:
• Peso: 85kg, Altura: 1.75m (hoje)
• Glicemia: 95 mg/dL em 15/01/2025
• Paciente relatou melhora da dor lombar após sessão
• Prescrição: Dipirona 500mg 6/6h por 5 dias`}
        className="min-h-[300px] max-h-[600px] resize-y font-mono text-sm"
        disabled={disabled}
        aria-label="Entrada de texto livre para observações clínicas"
      />

      {isWarning && characterCount > 0 && (
        <p className="text-sm text-yellow-600">
          ⚠️ Texto muito curto. Adicione mais detalhes para melhor análise.
        </p>
      )}

      <div className="flex items-center justify-between gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePaste}
          disabled={disabled}
          className="gap-2"
        >
          <ClipboardPaste className="h-4 w-4" />
          Colar
        </Button>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={disabled}
            className="gap-2"
          >
            <X className="h-4 w-4" />
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={disabled || !isValid}
            className="gap-2"
          >
            <Sparkles className="h-4 w-4" />
            Processar Texto
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Dica: Use Ctrl+Enter para processar rapidamente
      </p>
    </div>
  );
}
